import { useEffect, useMemo, useRef, useState } from 'react';
import { COUNTRIES, countryOf, defaultCountry } from './countries';

// Digits are a dial code, matched from the start ("1" must not find +213); anything
// else is a name or ISO code. Every match is listed; the menu scrolls.
const matchesQuery = (c, q) => (/^\d+$/.test(q)
  ? c.dial.startsWith(q)
  : `${c.name} ${c.iso}`.toLowerCase().includes(q));

// The country for a phone number, picked the way Settings picks a timezone: type to
// search, the matches in a scrolling menu, arrows + Enter. A native <select> of 165 rows is a
// scroll through the alphabet; nobody wants Zimbabwe to be the last thing they see.
export default function CountryField({ value, onChange, disabled = false }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const box = useRef(null);
  const here = useMemo(() => defaultCountry(), []);

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = query.trim().toLowerCase().replace(/^\+/, '');
  const matches = q
    ? COUNTRIES.filter((c) => matchesQuery(c, q))
    : [...COUNTRIES.filter((c) => c.iso === here), ...COUNTRIES.filter((c) => c.iso !== here)];
  const current = countryOf(value);
  const shown = `${current.name} +${current.dial}`;

  const pick = (iso) => { onChange(iso); setQuery(''); setOpen(false); setCursor(0); };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setCursor((c) => Math.min(c + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && open && matches[cursor]) { e.preventDefault(); pick(matches[cursor].iso); }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  return (
    <div ref={box} className="join-country">
      <input
        className="join-country__input"
        aria-label="Country"
        role="combobox"
        aria-expanded={open && matches.length > 0}
        data-value={value}
        disabled={disabled}
        value={open ? query : shown}
        placeholder={open ? shown : ''}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setCursor(0); }}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onKeyDown={onKeyDown}
      />
      {open && matches.length > 0 && (
        <div className="join-country__menu" role="listbox">
          {matches.map((c, i) => (
            <div
              key={c.iso}
              role="option"
              aria-selected={i === cursor}
              className={`join-country__opt${i === cursor ? ' is-on' : ''}`}
              ref={i === cursor ? (el) => el?.scrollIntoView?.({ block: 'nearest' }) : undefined}
              onMouseDown={(e) => { e.preventDefault(); pick(c.iso); }}
              onMouseEnter={() => setCursor(i)}
            >
              <span>{!q && c.iso === here ? `This device: ${c.name}` : c.name}</span>
              <span className="join-country__n">+{c.dial}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
